import { useRef, useState } from "react"
import axios from "axios"
import toast from "react-hot-toast"
import { readSheet } from "read-excel-file/browser"

/* ── Parsing helpers (mengikuti pola import-legacy-mapping) ── */
const stripControl = (v) =>
    String(v ?? "")
        .split("")
        .map((c) => {
            const code = c.charCodeAt(0)
            return code < 32 || (code >= 127 && code <= 159) ? " " : c
        })
        .join("")

const normHeader = (v) =>
    stripControl(v).replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().toUpperCase()

const detectDelimiter = (line) => {
    const candidates = [";", ",", "\t", "|"]
    let best = ","
    let bestCount = 0
    candidates.forEach((d) => {
        const count = (line.match(new RegExp(`\\${d}`, "g")) || []).length
        if (count > bestCount) {
            best = d
            bestCount = count
        }
    })
    return best
}

const parseCsvLine = (line, delimiter) => {
    const values = []
    let current = ""
    let quoted = false
    for (let i = 0; i < line.length; i += 1) {
        const char = line[i]
        const next = line[i + 1]
        if (char === '"' && quoted && next === '"') {
            current += '"'
            i += 1
            continue
        }
        if (char === '"') {
            quoted = !quoted
            continue
        }
        if (char === delimiter && !quoted) {
            values.push(current)
            current = ""
            continue
        }
        current += char
    }
    values.push(current)
    return values.map((v) => stripControl(v).trim())
}

const rowsToObjects = (matrix) => {
    if (!Array.isArray(matrix) || matrix.length < 1) return []
    const headers = matrix[0].map((h) => normHeader(String(h ?? "")))
    return matrix
        .slice(1)
        .filter((vals) => vals.some((v) => String(v ?? "").trim() !== ""))
        .map((vals) => {
            const obj = {}
            headers.forEach((h, i) => {
                obj[h] = vals[i] === null || vals[i] === undefined ? "" : String(vals[i]).trim()
            })
            obj.__values = vals.map((v) => (v == null ? "" : String(v).trim()))
            return obj
        })
}

const parseCsv = (text) => {
    const lines = String(text || "")
        .replace(/^﻿/, "")
        .replace(/\r\n/g, "\n")
        .replace(/\r/g, "\n")
        .split("\n")
        .filter((l) => l.trim() !== "")
    if (lines.length < 1) return []
    const delimiter = detectDelimiter(lines[0])
    return rowsToObjects(lines.map((l) => parseCsvLine(l, delimiter)))
}

const isXlsx = (file) => {
    const name = String(file?.name || "").toLowerCase()
    return name.endsWith(".xlsx") || name.endsWith(".xls")
}

const readFileAsText = (file) =>
    new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result || "")
        reader.onerror = () => reject(reader.error || new Error("Gagal membaca file"))
        reader.readAsText(file)
    })

/* ── Ekstraksi identifier per baris ── */
const pickId = (r) =>
    r["ID"] || r["PRODUCT ID"] || r["PRODUCT UNIT ID"] || r["PRODUCT MODEL ID"] || ""
const pickName = (r) => r["NAME"] || r["NAMA"] || r["LONG NAME"] || ""
const pickIdentifier = (r) =>
    r["SKU"] || r["KODE"] || r["CODE"] || r["NAME"] || r["NAMA"] ||
    (r.__values || []).find((v) => String(v).trim() !== "") || ""

export default function ImportTarget({ level, onImport, disabled }) {
    const inputRef = useRef(null)
    const [dragging, setDragging] = useState(false)
    const [busy, setBusy] = useState(false)

    const handleFile = async (file) => {
        if (!file) return
        if (!level) {
            toast.error("Pilih level produk terlebih dahulu")
            return
        }

        setBusy(true)
        try {
            const rows = isXlsx(file)
                ? rowsToObjects(await readSheet(file))
                : parseCsv(await readFileAsText(file))

            if (!rows.length) {
                toast.error("File kosong atau format tidak dikenali")
                return
            }

            // Baris dengan kolom ID → pakai langsung. Sisanya (SKU/nama) → resolve via backend.
            const direct = []
            const identifiers = []
            rows.forEach((r) => {
                const id = pickId(r)
                if (id) {
                    direct.push({ id, name: pickName(r) || id })
                } else {
                    const ident = pickIdentifier(r)
                    if (ident) identifiers.push(ident)
                }
            })

            let matched = [...direct]
            let unmatchedCount = 0

            if (identifiers.length) {
                const url = `${process.env.REACT_APP_INVENTORY_API_URL}/discount/resolve-targets`
                const { data } = await axios.post(url, { level, identifiers })
                matched = matched.concat(data.data.matched ?? [])
                unmatchedCount = data.data.unmatched_count ?? 0
            }

            // dedupe by id
            const seen = new Set()
            const finalItems = matched.filter((m) => {
                const k = String(m.id)
                if (!m.id || seen.has(k)) return false
                seen.add(k)
                return true
            })

            if (!finalItems.length) {
                toast.error("Tidak ada produk yang cocok dari file")
                return
            }

            onImport(finalItems, { unmatchedCount })
            toast.success(
                `${finalItems.length} produk dari file` +
                    (unmatchedCount ? `, ${unmatchedCount} tidak ditemukan` : "")
            )
        } catch (e) {
            console.error(e)
            toast.error(e.response?.data?.message ?? "Gagal membaca/impor file")
        } finally {
            setBusy(false)
            if (inputRef.current) inputRef.current.value = ""
        }
    }

    return (
        <div>
            <div
                onClick={() => !disabled && !busy && inputRef.current?.click()}
                onDragOver={(e) => {
                    e.preventDefault()
                    if (!disabled) setDragging(true)
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                    e.preventDefault()
                    setDragging(false)
                    if (!disabled && !busy) handleFile(e.dataTransfer.files?.[0])
                }}
                className={`flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-xl transition ${
                    disabled
                        ? "opacity-50 cursor-not-allowed border-neutral-300"
                        : dragging
                        ? "border-orange-500 bg-orange-50 cursor-pointer"
                        : "border-neutral-300 hover:border-neutral-400 cursor-pointer"
                }`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                    className="hidden"
                    disabled={disabled || busy}
                    onChange={(e) => handleFile(e.target.files?.[0])}
                />
                <div className="space-y-1 text-center">
                    <svg
                        className="w-10 h-10 mx-auto text-neutral-400"
                        stroke="currentColor"
                        fill="none"
                        viewBox="0 0 48 48"
                        aria-hidden="true"
                    >
                        <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <div className="text-sm text-neutral-600">
                        <span className="font-medium text-neutral-900">
                            {busy ? "Memproses..." : "Upload file"}
                        </span>
                        <span className="pl-1">atau drag &amp; drop di sini</span>
                    </div>
                    <p className="text-xs text-neutral-500">
                        CSV / XLSX — kolom <b>id</b>, atau <b>sku</b>/<b>name</b> (dicocokkan otomatis)
                    </p>
                </div>
            </div>
        </div>
    )
}
